import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Camera, AlertCircle } from 'lucide-react';
import Button from '../common/Button';

const QRScannerModalV2 = ({ isOpen, onClose, onScan, loading }) => {
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [scanCooldown, setScanCooldown] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  // Define functions in dependency order (bottom-up)
  const handleQRCodeDetected = useCallback((qrCode) => {
    if (qrCode === lastScannedCode || scanCooldown) {
      return;
    }

    console.log('🔍 Enhanced Scanner - QR Code detected:', qrCode);
    setLastScannedCode(qrCode);
    setScanCooldown(true);
    
    // Call parent handler
    onScan(qrCode);
    
    // Reset after cooldown
    setTimeout(() => {
      setLastScannedCode(null);
      setScanCooldown(false);
      console.log('🔄 Enhanced Scanner ready for next QR code');
    }, 2000);
  }, [lastScannedCode, scanCooldown, onScan]);

  const scanForQRCode = useCallback(() => {
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        return;
      }

      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      // Use jsQR library for QR code detection
      if (window.jsQR) {
        const code = window.jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code && code.data) {
          handleQRCodeDetected(code.data);
        }
      } else {
        console.warn('⚠️ jsQR library not loaded');
      }
    } catch (err) {
      console.error('❌ QR scanning error:', err);
    }
  }, [handleQRCodeDetected]);


  const stopCamera = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };


  // Initialize camera when modal opens
  useEffect(() => {
    const initializeCamera = async () => {
      try {
        console.log('📹 Starting Enhanced Scanner camera...');
        setError(null);
        
        // Stop existing stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }

        // Get camera stream
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: facingMode,
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });

        console.log('✅ Camera stream obtained');
        streamRef.current = stream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          // Wait for video to be ready
          videoRef.current.onloadedmetadata = () => {
            console.log('📺 Video metadata loaded');
            videoRef.current.play().then(() => {
              console.log('▶️ Video playing');
              
              // Start scanning directly here
              console.log('🚀 Starting Enhanced Scanner...');
              if (scanIntervalRef.current) {
                clearInterval(scanIntervalRef.current);
              }

              scanIntervalRef.current = setInterval(() => {
                if (videoRef.current && canvasRef.current && !scanCooldown) {
                  scanForQRCode();
                }
              }, 200); // Scan every 200ms
            }).catch(err => {
              console.error('❌ Video play error:', err);
              setError('Failed to start video playback');
            });
          };
        }
      } catch (err) {
        console.error('❌ Camera error:', err);
        setError('Camera access denied or not available. Please check your browser permissions.');
      }
    };

    if (isOpen) {
      console.log('🔄 Enhanced Scanner modal opened');
      console.log('🔍 Checking jsQR library:', !!window.jsQR);
      
      // If jsQR isn't loaded, try to load it dynamically
      if (!window.jsQR) {
        console.log('⏳ Loading jsQR library dynamically...');
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
        script.onload = () => {
          console.log('✅ jsQR loaded dynamically');
          initializeCamera();
        };
        script.onerror = () => {
          console.error('❌ Failed to load jsQR dynamically');
          setError('Failed to load scanner library. Please try the Standard Scanner.');
        };
        document.head.appendChild(script);
      } else {
        initializeCamera();
      }
    } else {
      console.log('❌ Enhanced Scanner modal closed');
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode, scanCooldown, scanForQRCode]);

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    setLastScannedCode(null);
    setScanCooldown(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <Camera className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                <span className="hidden sm:inline">Enhanced QR Scanner</span>
                <span className="sm:hidden">Enhanced Scanner</span>
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors p-1"
              disabled={loading}
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Scanner */}
          <div className="px-3 sm:px-6 py-4 sm:py-6">
            {error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-red-800 mb-1">Scanner Error</h4>
                  <p className="text-sm text-red-700">{error}</p>
                  <p className="text-xs text-red-600 mt-2">
                    {error.includes('Camera') 
                      ? 'Make sure you\'ve granted camera permissions in your browser settings.'
                      : 'Try using the Standard Scanner instead.'
                    }
                  </p>
                </div>
              </div>
            ) : !window.jsQR ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">Loading Enhanced Scanner...</h4>
                  <p className="text-sm text-yellow-700">Please wait while the enhanced scanner loads, or close this and try the Standard Scanner.</p>
                  <div className="mt-3">
                    <div className="animate-pulse flex space-x-1">
                      <div className="rounded-full bg-yellow-400 h-2 w-2"></div>
                      <div className="rounded-full bg-yellow-400 h-2 w-2"></div>
                      <div className="rounded-full bg-yellow-400 h-2 w-2"></div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover bg-gray-900"
                    style={{ 
                      minHeight: '200px',
                      backgroundColor: '#111827'
                    }}
                    onLoadedMetadata={() => console.log('📺 Video metadata loaded')}
                    onPlay={() => console.log('▶️ Video started playing')}
                    onError={(e) => console.error('❌ Video error:', e)}
                  />
                  <canvas
                    ref={canvasRef}
                    className="hidden"
                  />
                  
                  {/* Scanning overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-2 sm:inset-4 border-2 border-white border-dashed rounded-lg opacity-50"></div>
                  </div>
                  
                  {(loading || scanCooldown) && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="text-white text-center px-4">
                        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-white mx-auto mb-2"></div>
                        <p className="text-sm sm:text-base">{loading ? 'Processing...' : 'Ready for next scan...'}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <p className="text-sm text-blue-800 text-center">
                    📱 Position the QR code within the camera frame
                  </p>
                  <p className="text-xs text-blue-600 text-center mt-1">
                    <span className="hidden sm:inline">Advanced scanning - ready for multiple codes</span>
                    <span className="sm:hidden">Ready for multiple codes</span>
                  </p>
                </div>

                <div className="flex justify-center">
                  <Button
                    variant="secondary"
                    onClick={toggleCamera}
                    className="gap-2 w-full sm:w-auto"
                    disabled={loading}
                  >
                    <Camera className="h-4 w-4" />
                    <span className="hidden xs:inline">Switch Camera</span>
                    <span className="xs:hidden">Switch</span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScannerModalV2;
